import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MobileShippingAddressPage.css';

const STORAGE_KEY = 'mobile-shipping-addresses';
const MAX = 5;

const readList = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v : [];
  } catch (_) {
    return [];
  }
};

const writeList = (list) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX)));
  } catch (_) {
    /* ignore */
  }
};

const DocIllustration = () => (
  <svg className="msa-empty-icon" viewBox="0 0 100 90" fill="none" aria-hidden>
    <rect x="22" y="12" width="56" height="70" rx="4" stroke="currentColor" strokeWidth="2" />
    <path d="M32 28h36M32 40h36M32 52h24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const emptyForm = () => ({
  name: '',
  phone: '',
  line1: '',
  city: '',
  state: '',
  pin: '',
  country: 'India',
});

const MobileShippingAddressPage = () => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const reload = useCallback(() => setAddresses(readList()), []);

  useEffect(() => {
    reload();
  }, [reload]);

  const saveAddress = (e) => {
    e.preventDefault();
    if (addresses.length >= MAX) return;
    const entry = {
      id: `addr-${Date.now()}`,
      ...form,
    };
    const next = [...addresses, entry];
    setAddresses(next);
    writeList(next);
    setForm(emptyForm());
    setModalOpen(false);
  };

  const openAdd = () => {
    if (addresses.length >= MAX) return;
    setModalOpen(true);
  };

  return (
    <div className="msa-page">
      <header className="msa-header">
        <button type="button" className="msa-back" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="msa-title">Shipping address</h1>
        <button type="button" className="msa-support" onClick={() => navigate('/my-support')} aria-label="Support">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
            <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
          </svg>
        </button>
      </header>

      {addresses.length === 0 ? (
        <div className="msa-empty">
          <DocIllustration />
          <p className="msa-empty-title">Seems like you haven&apos;t saved any addresses yet!</p>
          <p className="msa-empty-sub">Save up to 5 addresses and you can edit them at any time.</p>
        </div>
      ) : (
        <div className="msa-list">
          {addresses.map((a) => (
            <div key={a.id} className="msa-card">
              <p className="msa-card-name">{a.name}</p>
              <p className="msa-card-line">{a.line1}</p>
              <p className="msa-card-line">
                {a.city}
                {a.state ? `, ${a.state}` : ''}
                {a.pin ? ` ${a.pin}` : ''}
              </p>
              <p className="msa-card-line">{a.country}</p>
              <p className="msa-card-line">{a.phone}</p>
            </div>
          ))}
        </div>
      )}

      <div className="msa-footer">
        <button type="button" className="msa-add-btn" onClick={openAdd} disabled={addresses.length >= MAX}>
          {addresses.length >= MAX ? 'Maximum 5 addresses saved' : 'Add a new address'}
        </button>
      </div>

      {modalOpen && (
        <div className="msa-overlay" role="presentation" onClick={(ev) => ev.target === ev.currentTarget && setModalOpen(false)}>
          <form className="msa-modal" onSubmit={saveAddress} onClick={(e) => e.stopPropagation()}>
            <h2>New address</h2>
            {['name', 'phone', 'line1', 'city', 'state', 'pin', 'country'].map((key) => {
              const labels = {
                name: 'Full name',
                phone: 'Phone',
                line1: 'Address line',
                city: 'City',
                state: 'State / Province',
                pin: 'PIN / ZIP',
                country: 'Country',
              };
              return (
                <div key={key} className="msa-field">
                  <label htmlFor={`msa-${key}`}>{labels[key]}</label>
                  <input
                    id={`msa-${key}`}
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    required={['name', 'line1', 'city', 'pin'].includes(key)}
                    autoComplete="shipping street-address"
                  />
                </div>
              );
            })}
            <div className="msa-modal-actions">
              <button type="button" className="msa-btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="msa-btn-primary">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default MobileShippingAddressPage;
